import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Upload, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useSession } from "@/context/SessionContext";

export default function Palette() {
  const navigate = useNavigate();
  const { session, update } = useSession();
  const [palettes, setPalettes] = useState(session.palettes || []);
  const [chosen, setChosen] = useState(session.chosen_palette?.id || null);
  const [loading, setLoading] = useState(!session.palettes?.length);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (session.palettes?.length) return;
    api.post("/palettes", { urls: session.selected_styles.map((s) => s.url) })
      .then(({ data }) => setPalettes(data))
      .catch(() => toast.error("Couldn't read colours from your picks."))
      .finally(() => setLoading(false));
  }, [session.palettes, session.selected_styles]);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/palettes/upload", fd);
      setPalettes((p) => [{ ...data, name: `${data.name} · yours` }, ...p]);
      setChosen(data.id);
    } catch {
      toast.error("That image didn't work — try a JPG or PNG.");
    }
    setUploading(false);
    e.target.value = "";
  };

  const next = async () => {
    await update({ palettes, chosen_palette: palettes.find((p) => p.id === chosen), step: Math.max(session.step, 3) });
    navigate("/about");
  };

  return (
    <main className="max-w-6xl mx-auto px-6 sm:px-10 pt-12 pb-40">
      <div className="max-w-3xl">
        <p className="eyebrow rise">Step three · Colour</p>
        <h1 className="font-display font-light text-4xl sm:text-5xl lg:text-6xl leading-tight mt-4 rise d1">What palette is on your mind?</h1>
        <p className="text-neutral-500 mt-4 rise d2">We pulled these straight from the spaces you saved. Pick the one that feels right — or bring your own.</p>
      </div>

      <div className="flex items-center gap-3 mt-10">
        <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={upload} data-testid="palette-upload-input" />
        <button data-testid="palette-upload-button" onClick={() => fileRef.current?.click()} disabled={uploading} className="btn-ghost">
          {uploading ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />} Want to upload your choice?
        </button>
      </div>

      {loading ? (
        <div data-testid="palette-loading" className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {[0, 1, 2].map((i) => <div key={i} className="h-56 rounded-3xl bg-neutral-100 animate-pulse" />)}
        </div>
      ) : (
        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {palettes.map((p, i) => (
            <button
              key={p.id}
              data-testid={`palette-card-${i}`}
              onClick={() => setChosen(p.id)}
              className={`text-left rounded-3xl border overflow-hidden transition-[border-color,transform] duration-300 hover:-translate-y-0.5 ${chosen === p.id ? "border-neutral-900" : "border-neutral-200"}`}
            >
              <div className="flex h-36">
                {p.colors.map((c) => <div key={c} style={{ background: c }} className="flex-1" />)}
              </div>
              <div className="p-5 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-medium">{p.name}</div>
                  <div className="font-mono text-[11px] text-neutral-400 mt-1 truncate">{p.colors.join("  ")}</div>
                </div>
                {p.source !== "upload" ? (
                  <img src={p.source} alt="" className="h-11 w-11 rounded-xl object-cover shrink-0" />
                ) : null}
                <span className={`h-8 w-8 rounded-full flex items-center justify-center shrink-0 border transition-colors ${chosen === p.id ? "bg-neutral-900 text-white border-neutral-900" : "border-neutral-300 text-transparent"}`}><Check size={14} /></span>
              </div>
            </button>
          ))}
        </div>
      )}

      <div className="fixed bottom-0 inset-x-0 z-30 pointer-events-none">
        <div className="max-w-3xl mx-auto px-4 pb-6 flex justify-end">
          <button data-testid="palette-continue-button" onClick={next} disabled={!chosen} className="btn-ink h-14 px-8 pointer-events-auto shadow-2xl">
            Tell us about you <ArrowRight size={18} />
          </button>
        </div>
      </div>
    </main>
  );
}
